import * as React from 'react';
import { Typography } from '@mui/material';
import { useFormikContext } from 'formik';
import { Option } from '../../interfaces';
import SelectInput from './SelectInput';
import {
  CreateAptValues,
  ImageItem,
  colorOptions,
} from '../../utils/form/appointmentForm';
import TextareaInput from './TextareaInput';
import ImageInput from './ImageInput';
import useUser from '../../hooks/global/useUser';
import useSizeOptions from '../../hooks/tattoos/useSizeOptions';
import useLocationOptions from '../../hooks/tattoos/useLocationOptions';

interface Props {
  images: ImageItem[];
  setImages: React.Dispatch<React.SetStateAction<ImageItem[]>>;
  // Off when a parent section already provides the heading
  showTitle?: boolean;
}

export default function TattooDetails({
  images,
  setImages,
  showTitle = true,
}: Props) {
  const { values, setFieldValue, setValues } =
    useFormikContext<CreateAptValues>();
  const { user } = useUser();
  const { sizeOptions } = useSizeOptions();
  const { locationOptions, isLoading: isLoadingLocations } =
    useLocationOptions();

  const service = user?.business?.services?.find(
    s => String(s.serviceId) === String(values.serviceId),
  );

  const piercingLocations = React.useMemo(
    () => service?.piercingLocations ?? [],
    [service?.piercingLocations],
  );

  const categoryOptions = React.useMemo<Option[]>(() => {
    if (!piercingLocations) return [];
    const uniqueCategories = Array.from(
      new Set(piercingLocations.map(location => location.locationCategory)),
    );
    return uniqueCategories.map(category => ({
      value: category,
      label: category,
    }));
  }, [piercingLocations]);

  const typeOptions = React.useMemo<Option[]>(() => {
    if (!piercingLocations || !values.locationCategory) return [];
    const filteredLocations = piercingLocations.filter(
      location => location.locationCategory === values.locationCategory,
    );
    return filteredLocations.map(location => ({
      value: String(location.locationId),
      label: location.locationType,
    }));
  }, [piercingLocations, values.locationCategory]);

  React.useEffect(() => {
    const isValidPiercingLocation = service?.piercingLocations?.find(
      location => location.locationId.toString() === values.piercingLocationId,
    );
    if (!isValidPiercingLocation) {
      setValues(prev => ({
        ...prev,
        locationCategory: '',
        piercingLocationId: '',
      }));
    }
  }, [service?.piercingLocations, setValues, values.piercingLocationId]);

  const isLockedPiercingLocation =
    service?.serviceCategory === 'PIERCING' &&
    service?.piercingLocations?.length === 1;
  React.useEffect(() => {
    if (isLockedPiercingLocation) {
      setValues(prev => ({
        ...prev,
        locationCategory: service?.piercingLocations![0].locationCategory,
        piercingLocationId:
          service?.piercingLocations![0].locationId.toString(),
      }));
    }
  }, [isLockedPiercingLocation, service?.piercingLocations, setValues]);

  const selectedLocation = locationOptions?.find(
    loc => loc.name === values.tattooLocation,
  );
  const showSideDropdown = selectedLocation?.askForSide;

  const sideOptions = React.useMemo<Option[]>(() => {
    if (!selectedLocation) return [];
    const locationName = selectedLocation.name;
    return [
      { value: 'Left', label: `Left ${locationName}` },
      { value: 'Right', label: `Right ${locationName}` },
    ];
  }, [selectedLocation]);

  // clear bodySide when location changes to one that doesn't need side.
  React.useEffect(() => {
    if (!isLoadingLocations && !showSideDropdown && values.bodySide) {
      setFieldValue('bodySide', '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSideDropdown, isLoadingLocations]);

  return (
    <div className="input-group appointment-details">
      {values.serviceId === '0' && (
        <>
          {showTitle && (
            <Typography variant="h2" component="h2">
              Tattoo Details
            </Typography>
          )}
          <div className="inputs">
            <div className="input-row">
              <SelectInput
                values={values}
                setFieldValue={setFieldValue}
                name="tattooLocation"
                label="Location"
                options={
                  (locationOptions ?? []).map(loc => ({
                    value: loc.name,
                    label: loc.name,
                  })) as Option[]
                }
                placeholder="Select body area"
                disabled={false}
              />
              {showSideDropdown && (
                <SelectInput
                  values={values}
                  setFieldValue={setFieldValue}
                  name="bodySide"
                  label="Side"
                  options={sideOptions}
                  placeholder="Select side"
                  disabled={false}
                />
              )}
            </div>
            <div className="input-row">
              <SelectInput
                values={values}
                setFieldValue={setFieldValue}
                name="tattooSize"
                label="Size"
                options={
                  (sizeOptions ?? []).map(size => ({
                    value: size.value,
                    label: size.name,
                  })) as Option[]
                }
                placeholder="Select size"
                disabled={false}
              />
            </div>
            <div className="input-row">
              <SelectInput
                values={values}
                setFieldValue={setFieldValue}
                name="tattooColor"
                label="Color"
                options={colorOptions}
                placeholder="Select color"
                disabled={false}
              />
            </div>
            <div className="input-row">
              <TextareaInput
                name="tattooDetails"
                label="Description"
                placeholder="Optional. Enter a description."
              />
            </div>
            <div className="input-row">
              <ImageInput
                name="tattooImages"
                images={images}
                setImages={setImages}
              />
            </div>
          </div>
        </>
      )}
      {values.serviceId !== '0' && (
        <>
          {showTitle && (
            <Typography variant="h2" component="h2">
              Service Details
            </Typography>
          )}
          <div className="inputs">
            {service?.serviceCategory === 'PIERCING' && (
              <div className="input-row">
                {categoryOptions && (
                  <SelectInput
                    values={values}
                    setFieldValue={setFieldValue}
                    name="locationCategory"
                    label="Location"
                    options={categoryOptions}
                    placeholder="Select body area"
                    disabled={isLockedPiercingLocation}
                  />
                )}
                {typeOptions && (
                  <SelectInput
                    values={values}
                    setFieldValue={setFieldValue}
                    name="piercingLocationId"
                    label="Detail"
                    options={typeOptions}
                    placeholder="Location detail"
                    disabled={isLockedPiercingLocation}
                  />
                )}
              </div>
            )}
            <div className="input-row">
              <TextareaInput
                name="tattooDetails"
                label="Description"
                placeholder="Optional. Enter a description."
              />
            </div>

            <div className="input-row">
              <ImageInput
                name="tattooImages"
                images={images}
                setImages={setImages}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
}
